import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {App, Reports, TOption} from 'pwm-shared';
import {API} from 'pwm-shared/src/types';
import {useApi} from '../hooks/use-api';
import {useMounted} from '../hooks/use-mounted';
import {useAppSettingsApi} from '../pages/app/appSettings.api';
import {PWMStorage} from '../utils/storage-util';
import {AxiosInstance} from 'axios';

const ClassificationsRoute: App.ClassificationsEndpoint = '/app/options/classifications';
const endpoint: Reports.API.Endpoint = 'reports';
const getKeyAction: Reports.API.GetReportKeyAction = 'getKey';

// init local storage
const ClassificationKey = 'pwm-classifications';
const ReportKey = 'pwm-reportKey';

const defaultValue: IAppDataContext = {
  async classifications() {return [];},
  async reportKey() {return '';}
}

export const AppDataContext = React.createContext<IAppDataContext>(defaultValue);
AppDataContext.displayName = 'AppDataContext';

const storedClassifications = PWMStorage.getItem(ClassificationKey) || [];
const storedReportKey = PWMStorage.getItem(ReportKey) || '';

export const AppDataProvider: React.FC<{children: any}> = (props) => {
  const [classifications, setClassifications] = React.useState<TOption[]>(storedClassifications);
  const [reportKey, setReportKey] = React.useState<string>(storedReportKey);

  const classificationsRef = useRef(classifications);
  const reportKeyRef = useRef(reportKey);

  useEffect(() => {
    classificationsRef.current = classifications;
  }, [classifications]);
  useEffect(() => {
    reportKeyRef.current = reportKey;
  }, [reportKey]);

  const api = useApi();
  const settingsApi = useAppSettingsApi();
  const mounted = useMounted();

  useEffect(() => {
    if(settingsApi && mounted()) {
      getData();
    } else {
      console.log('API Provider not ready');
    }
  }, [settingsApi, mounted]);

  const getClassifications = useCallback(async (client: AxiosInstance): Promise<TOption[]> => {
    const {data} = await client.get<API.Response<TOption[]>>(ClassificationsRoute);
    PWMStorage.setItem(ClassificationKey, data.data);
    setClassifications(data.data);
    return data.data;
  }, []);

  const getClassificationData = useCallback(async (refresh: boolean = false) => {
    if (!refresh && classificationsRef.current.length > 0) return classificationsRef.current;
    const client = api?.getClient()!;
    return await getClassifications(client);
  }, [api, getClassifications]);

  const getReportToken = useCallback(async (client: AxiosInstance): Promise<string> => {
    const {data} = await client.get(`${endpoint}/${getKeyAction}`);
    PWMStorage.setItem(ReportKey, data.data.key);
    setReportKey(data.data.key);
    return data.data.key;
  }, []);

  const getReportTokenData = useCallback(async (refresh: boolean = false) => {
    if (!refresh && reportKeyRef.current.length > 0) return reportKeyRef.current;
    const client = api?.getClient()!;
    return getReportToken(client);
  }, [api, getReportToken]);

  const getData = useCallback(async () => {
    await Promise.all([getClassificationData(), getReportTokenData()]);
  }, [getClassificationData, getReportTokenData]);

  const value: IAppDataContext = useMemo(() => ({
    async classifications() {
      return await getClassificationData();
    },
    async reportKey() {
      return await getReportTokenData();
    }
  }), [getClassificationData, getReportTokenData, settingsApi]);

  return (
    <AppDataContext.Provider value={value}>{props.children}</AppDataContext.Provider>
  )
}

interface IAppDataContext {
  classifications(): Promise<TOption[]>;
  reportKey(): Promise<string>;
}
